// client/src/orders/OrderList.tsx
import React, { Fragment, useCallback, SyntheticEvent } from 'react';
import {
    List,
    Datagrid,
    TextField,
    NumberField,
    EditButton,
    useListContext,
    FunctionField
} from 'react-admin';
import { Tabs, Tab, useMediaQuery, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import useAuthorize from '../hooks/useAuthorize';
import { UserRole, IOrder } from '../types/types';
import { format, parseISO } from 'date-fns';

const tabs = [
    { id: '1', name: 'Pending' },
    { id: '2', name: 'In-Transit' },
    { id: '3', name: 'Completed' },
];

const TabbedDatagrid: React.FC = () => {
    const { filterValues, setFilters, displayedFilters } = useListContext();
    const theme: Theme = useTheme();
    const isXSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const handleChange = useCallback(
        (_event: SyntheticEvent, value: string) => {
            setFilters({ ...filterValues, status: value }, displayedFilters);
        },
        [filterValues, setFilters, displayedFilters]
    );

    // Link to the order history
    const OrderHistoryField: React.FC<{ record: IOrder }> = ({ record }) => (
        <a
            href={`#/orderhistory?filter=${encodeURIComponent(
                JSON.stringify({ order_id: record.id })
            )}`}
            onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                navigate(
                    `/orderhistory?filter=${encodeURIComponent(
                        JSON.stringify({ order_id: record.id })
                    )}`
                );
            }}
            style={{ 
                color: theme.palette.primary.main,
                textDecoration: 'none',
                cursor: 'pointer'
            }}
        >
            {record.history_count || 0}
        </a>
    );

    useAuthorize([UserRole.Admin, UserRole.ProductAdmin]);

    return (
        <Fragment>
            <Tabs 
                value={filterValues.status || '1'} 
                onChange={handleChange} 
                centered
                indicatorColor="primary"
                textColor="primary"
            >
                {tabs.map(choice => (
                    <Tab key={choice.id} label={choice.name} value={choice.id} />
                ))}
            </Tabs>

            <Datagrid rowClick="edit" bulkActionButtons={false}>
                <NumberField source="id" label="ID" />

                <FunctionField<IOrder>
                    label="Order Date"
                    sortBy="order_date"
                    render={record => {
                        console.log(record.order_date);
                        if (!record.order_date) return '-';
                        try {
                            // Parse ISO date and format to match bid format
                            const date = parseISO(record.order_date);
                            return format(date, 'dd-MM-yy HH:mm');
                        } catch (error) {
                            return record.order_date; // Return raw value if parsing fails
                        }
                    }}
                />
                
                <TextField source="customer_contact" label="Customer" />
                <TextField source="state_name" label="State" />
                <TextField source="city_name" label="City" />
                <TextField source="product_name" label="Product Name" />
                <TextField source="product_grade" label="Product Grade" />
                <NumberField source="order_quantity" label="Qty" />
                <NumberField 
                    source="order_price" 
                    label="Price" 
                    options={{ style: 'currency', currency: 'INR' }}
                />
                <NumberField 
                    source="order_total" 
                    label="Total" 
                    options={{ style: 'currency', currency: 'INR' }}
                />

                <FunctionField<IOrder>
                    label="History"
                    render={(record: IOrder) => <OrderHistoryField record={record} />}
                    sortable={false}
                />

                <EditButton />
            </Datagrid>
        </Fragment>
    );
};

export const OrderList: React.FC = props => (
    <List
        {...props}
        filterDefaultValues={{ status: '1' }}
        sort={{ field: 'order_date', order: 'DESC' }}
        perPage={25}
    >
        <TabbedDatagrid />
    </List>
);

export default OrderList;